import { TooltipProps } from 'recharts';
import { getComplianceSubcategoryColor } from '../../../../utilities/colorUtilities';
import {  FundSummaryWithTypes } from '../types/chart.types';

interface CustomTooltipProps {
  payload?: any[];
  label?: string;
  colorMap: Record<string, string>;
  delay?: number;
}


interface CustomTooltipProps extends TooltipProps<number, string> {}

export const BasicStackedTooltip: React.FC<
  Omit<CustomTooltipProps, 'colorMap'>
> = ({ payload, label }) => {
  if (!payload || payload.length === 0) return null;

  // .payload.fullData is embedded in flattenedData
  const fullData = payload[0].payload.fullData as
    | Record<string, FundSummaryWithTypes>
    | undefined;

  if (!fullData) return null;

  return (
    <div className="w-130 rounded-lg border border-gray-200 bg-white p-3 shadow-md">
      <p className="mb-2 text-sm font-bold text-gray-800">
        {(() => {
          const date = new Date(label || '');
          const dayOfWeek = date.toLocaleDateString('en-US', {
            weekday: 'long',
          });
          return `${dayOfWeek}, ${label}`;
        })()}
      </p>
      <div className="space-y-2">
        {Object.entries(fullData).map(([subcategory, summary]) => {
          const failCount = summary.Fail.count;
          const alertCount = summary.Alert.count;
          const passCount = summary.Pass.count;
          const total = failCount + alertCount + passCount;

          if (total === 0) return null;

          return (
            <div
              key={subcategory}
              className="flex items-center justify-between"
            >
              <span
                className={`truncate rounded-full px-2 py-1 pr-2 text-xs font-medium outline outline-1 ${getComplianceSubcategoryColor(
                  subcategory,
                )}`}
              >
                {subcategory}
              </span>
              <div className="flex items-center gap-1 pl-4 text-[11px]">
                <span
                  className={`flex w-16 justify-between rounded px-1.5 py-0.5 font-semibold ${
                    failCount > 0
                      ? 'bg-fail/20 text-fail'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  <span>FAIL:</span>
                  <span>{failCount}</span>
                </span>
                <span
                  className={`flex w-16 justify-between rounded px-1.5 py-0.5 font-semibold ${
                    alertCount > 0
                      ? 'bg-alert/20 text-alert'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  <span>ALERT:</span>
                  <span>{alertCount}</span>
                </span>
                <span
                  className={`flex w-16 justify-between rounded px-1.5 py-0.5 font-semibold ${
                    passCount > 0
                      ? 'bg-pass/20 text-pass'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  <span>PASS:</span>
                  <span>{passCount}</span>
                </span>
                <span className="ml-1 w-8 rounded bg-gray-100 px-1.5 py-0.5 text-center text-gray-700">
                  {total}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 border-t pt-2 text-[11px] text-gray-500">
        Click to see fund breakdown
      </div>
    </div>
  );
};
